import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import Sidebar from "../../../sidebar/sidebar";
import Header from "../../../header/header";
import { injectKpiStyles } from "./kpiStyles";
import { useKpiData } from "../hooks/useKpiData";
import KpiPageHeader from "./KpiPageHeader";
import KpiFiltersBar from "./Kpifiltersbar";
import KpiFiltersModal from "./Kpifiltersmodal";
import KpiRow from "./KpiRow";
import BulkActionBar from "./BulkActionBar";
import AddDataModal from "./AddDataModal";
import ChatModal from "../../components/chatComponent/ChatModal";
import AuditTrailModal from "./AuditTrailModal";
import AssignModal from "./AssignModal";
import { apiCall } from "../../../../_services/apiCall";
import config from "../../../../config/config.json";

import BulkAssignModal from "./bulkAction/Bulkassignmodal";
import BulkReassignModal from "./bulkAction/Bulkreassignmodal";
import BulkPrefillModal from "./bulkAction/Bulkprefillmodal";
import BulkReminderModal from "./bulkAction/Bulkremindermodal";
import BulkAuditAssignModal from "./bulkAction/BulkAuditAssignModal";

// Import cascade filter matcher from KpiTagFilter
import { kpiMatchesCascadeSteps } from "./KpiTagFilter";

const SESSION_KEY = "quantative_list_state_v2";

const loadSavedState = () => {
    try {
        return JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null");
    } catch {
        return null;
    }
};

/* ─────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────── */
const QuantativeList = (props) => {
    const savedState = loadSavedState();
    const { moduleName } = useParams();
    const historyRouter = useHistory();

    useEffect(() => { injectKpiStyles(); }, []);

    const [sidebarExpanded, setSidebarExpanded] = useState(true);

    const [managementListValue, setManagementListValue] = useState([]);
    const [auditorType, setAuditorType] = useState([]);
    const isMounted = useRef(true);
    useEffect(() => () => { isMounted.current = false; }, []);

    const userId = JSON.parse(localStorage.getItem("user_temp_id"));

    const getDesignation = async () => {
        try {
            const { isSuccess, data } = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}getMasterData`,
                {},
                { userId },
                "GET"
            );
            if (isSuccess && isMounted.current) {
                const nonAuditorRoles = data?.data.filter(
                    (role) =>
                        !(
                            role.onlyauditor === true ||
                            role?.role_name === "Trainee" ||
                            role?.role_name === "Trainer"
                        )
                );
                const auditorRoles = data?.data.filter(
                    (role) => role.onlyauditor === true
                );
                setManagementListValue(nonAuditorRoles?.reverse());
                setAuditorType(auditorRoles?.reverse());
            }
        } catch (error) {
            console.error("Error fetching designation:", error);
        }
    };

    useEffect(() => {
        getDesignation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ──────────────────────────────────────────────────
       FILTERS
    ────────────────────────────────────────────────── */
    const [search, setSearch] = useState(savedState?.search || "");
    const [moduleFilter, setModuleFilter] = useState(savedState?.moduleFilter || moduleName || "");
    const [frameworkFilter, setFrameworkFilter] = useState(savedState?.frameworkFilter || "");
    const [fyFilter, setFyFilter] = useState(savedState?.fyFilter || "");
    const [expandedId, setExpandedId] = useState(savedState?.expandedId ?? null);

    // ── Cascade tag filter — restore Sets from serialized arrays ──
    const [filterSteps, setFilterSteps] = useState(() => {
        const saved = savedState?.filterSteps || [];
        return saved.map((s) => ({ key: s.key, values: new Set(s.values || []) }));
    });

    const [advFilters, setAdvFilters] = useState(
        savedState?.advFilters || { location_id: "", status: "", from_date: "", to_date: "" }
    );

    const advFilterChange = (key, val) => setAdvFilters((p) => ({ ...p, [key]: val }));
    const advFilterCount = Object.values(advFilters).filter(Boolean).length;

    const answerFilters = useMemo(() => ({
        location_id: advFilters.location_id,
        from_date: advFilters.from_date,
        to_date: advFilters.to_date,
    }), [advFilters.location_id, advFilters.from_date, advFilters.to_date]);

    /* ─────────────── hook ─────────────── */
    const {
        kpiList,
        isLoading,
        error,
        financialYears,
        locations,
        modules,
        frameworks,
        selectedFY,
        setSelectedFY,
        saveAnswer,
        refetch,
    } = useKpiData({ answerFilters });

    useEffect(() => {
        if (moduleName) setModuleFilter(moduleName);
    }, [moduleName]);

    useEffect(() => {
        if (selectedFY?.id && !fyFilter) {
            setFyFilter(String(selectedFY.id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFY?.id]);

    // ── Persist state to sessionStorage — serialize Sets to arrays ──
    useEffect(() => {
        const state = {
            search,
            moduleFilter,
            fyFilter,
            frameworkFilter,
            advFilters,
            expandedId,
            filterSteps: filterSteps.map((s) => ({ key: s.key, values: [...(s.values || [])] })),
        };
        const existing = loadSavedState() || {};
        const { scrollTop: _drop, ...rest } = existing;
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({ ...rest, ...state }));
    }, [search, moduleFilter, fyFilter, frameworkFilter, advFilters, expandedId, filterSteps]);

    const scrollContainerRef = useRef(null);
    const scrollRestored = useRef(false);

    useEffect(() => {
        if (scrollRestored.current) return;
        if (!expandedId) return;
        if (!kpiList.length) return;

        const doScroll = () => {
            const el = document.querySelector(`[data-kpi-id="${expandedId}"]`);
            if (!el) return false;
            el.scrollIntoView({ block: "center", behavior: "instant" });
            scrollRestored.current = true;
            return true;
        };

        let raf1, raf2, t;
        raf1 = requestAnimationFrame(() => {
            if (!doScroll()) {
                raf2 = requestAnimationFrame(() => {
                    if (!doScroll()) {
                        t = setTimeout(doScroll, 200);
                    }
                });
            }
        });

        return () => {
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
            clearTimeout(t);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kpiList.length]);

    const handleScroll = useCallback(() => { }, []);

    /* ─────────────── Filtered list using cascade matcher ─────────────── */
    const filtered = useMemo(() => {
        return kpiList.filter((item) => {
            if (search && !item.kpi_name.toLowerCase().includes(search.toLowerCase())) return false;
            if (moduleFilter && item.module !== moduleFilter) return false;
            if (
                frameworkFilter &&
                (!Array.isArray(item.mapFrameworkIds) ||
                    !item.mapFrameworkIds.includes(String(frameworkFilter)))
            ) return false;
            if (advFilters.status === "pending" && item.answered_date) return false;
            if (advFilters.status === "completed" && !item.answered_date) return false;
            // ── Cascade tag filter ──
            if (!kpiMatchesCascadeSteps(item, filterSteps)) return false;
            return true;
        });
    }, [kpiList, search, moduleFilter, frameworkFilter, advFilters.status, filterSteps]);

    const completedCount = filtered.filter((i) => i.answered_date).length;
    const pendingCount = filtered.length - completedCount;

    const selectedLocation = locations.find(
        (l) => l.id === advFilters.location_id
    ) || null;

    const [selectedIds, setSelectedIds] = useState(new Set());
    const allSelected = filtered.length > 0 && filtered.every((i) => selectedIds.has(i.id));

    const toggleOne = (id) =>
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    const toggleAll = () =>
        allSelected
            ? setSelectedIds(new Set())
            : setSelectedIds(new Set(filtered.map((i) => i.id)));
    const clearSelection = () => setSelectedIds(new Set());

    const selectedIdsArray = useMemo(() => [...selectedIds], [selectedIds]);

    /* ── Per-row modal state ── */
    const [showFiltersModal, setShowFiltersModal] = useState(false);
    const [modal, setModal] = useState({ type: null, item: null });
    const openModal = (type, item) => setModal({ type, item });
    const closeModal = () => setModal({ type: null, item: null });

    const [bulkModal, setBulkModal] = useState(null);
    const closeBulkModal = useCallback(() => setBulkModal(null), []);

    const handleBulkAction = useCallback((actionKey) => {
        setBulkModal(actionKey);
    }, []);

    /* ── Helpers for active filter step count ── */
    const activeFilterStepCount = filterSteps.filter((s) => s.values?.size > 0).length;
    const hasAnyFilter =
        search ||
        moduleFilter ||
        fyFilter ||
        frameworkFilter ||
        advFilterCount > 0 ||
        activeFilterStepCount > 0;

    /* ── Clear ALL filters including cascade tag steps ── */
    const clearAllFilters = () => {
        setSearch("");
        setModuleFilter("");
        setFyFilter("");
        setFrameworkFilter("");
        setAdvFilters({ location_id: "", status: "", from_date: "", to_date: "" });
        setFilterSteps([]);
        sessionStorage.removeItem(SESSION_KEY);
    };

    /* ── Handle FY change ── */
    const handleFyChange = (v) => {
        setFyFilter(v);
        if (v) {
            const fy = financialYears.find((f) => String(f.id) === String(v));
            if (fy) setSelectedFY({ id: fy.id, name: fy.financial_year_value || fy.name, financial_year_value: fy.financial_year_value });
        }
    };

    const handleOpenHistory = useCallback((item) => {
        const statePayload = {
            kpiName: item.kpi_name,
            financialYearId: selectedFY?.id,
            financialYearName: selectedFY?.name,
            locations: locations,
            fromDate: advFilters.from_date ?? "",
            toDate: advFilters.to_date ?? "",
            module: item.module,
            assignedTo: item.assigned_to,
            unitSetting: item.unitSetting ?? null,
        };
        sessionStorage.setItem(`ahp_state_${item.id}`, JSON.stringify(statePayload));
        historyRouter.push(`/data-managment/quantative/${item.id}`, statePayload);
    }, [historyRouter, selectedFY, locations, advFilters]);

    /* ─────────────── Column header helper ─────────────── */
    const ColLabel = ({ children, width, flex, align = "left" }) => (
        <div style={{
            ...(width ? { width, flexShrink: 0 } : {}),
            ...(flex ? { flex } : {}),
            padding: "0 10px",
            fontSize: 10,
            fontWeight: 700,
            color: "var(--ink-4)",
            textTransform: "uppercase",
            letterSpacing: "0.07em",
            fontFamily: "var(--font-mono)",
            textAlign: align,
        }}>
            {children}
        </div>
    );

    /* ─────────────────────────────────────────────────────────
       RENDER
    ───────────────────────────────────────────────────────── */
    return (
        <div
            className="kpi-root"
            style={{
                display: "flex",
                background: "#f0f2f5",
                minHeight: "100vh",
                fontFamily: "var(--font-sans)",
            }}
        >
            <div
                style={{
                    flex: sidebarExpanded ? "0 0 21%" : "0 0 60px",
                    position: "sticky",
                    top: 0,
                    height: "100vh",
                    background: "var(--surface)",
                    borderRight: "1px solid var(--rule)",
                    zIndex: 100,
                    transition: "flex 0.2s ease",
                }}
            >
                <Sidebar
                    dataFromParent={props.location?.pathname || "/dashboard"}
                    onSidebarToggle={(isOpen) => setSidebarExpanded(isOpen)}
                />
            </div>

            <main style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
                <Header />

                <KpiPageHeader
                    selectedFY={selectedFY}
                    totalCount={filtered.length}
                    completedCount={completedCount}
                    pendingCount={pendingCount}
                    selectedLocation={selectedLocation}
                    onClearLocation={() => advFilterChange("location_id", "")}
                    onOpenFilters={() => setShowFiltersModal(true)}
                    advFilterCount={advFilterCount}
                />

                <KpiFiltersBar
                    search={search}
                    onSearchChange={setSearch}
                    moduleFilter={moduleFilter}
                    onModuleChange={setModuleFilter}
                    fyFilter={fyFilter}
                    onFyChange={handleFyChange}
                    frameworkFilter={frameworkFilter}
                    onFrameworkChange={setFrameworkFilter}
                    financialYears={financialYears}
                    modules={modules}
                    frameworks={frameworks}
                    locations={locations}
                    advFilterCount={advFilterCount}
                    onOpenAdvanced={() => setShowFiltersModal(true)}
                    onClearAll={clearAllFilters}
                    // ── Cascade tag filter props ──────────────────────────
                    kpiList={kpiList}
                    filterSteps={filterSteps}
                    onFilterStepsChange={setFilterSteps}
                />

                <BulkActionBar
                    count={selectedIds.size}
                    selectedIds={selectedIdsArray}
                    onAction={handleBulkAction}
                    onDeselect={clearSelection}
                />

                <div
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    style={{ flex: 1, overflow: "auto", padding: "16px 24px 28px" }}
                >
                    {/* ── Active filter indicator chips ── */}
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, paddingLeft: 4 }}>
                        {selectedIds.size > 0 && (
                            <>
                                <span style={{ color: "#d1d5db", fontSize: 14, lineHeight: 1 }}>·</span>
                                <span style={{ fontSize: 12, color: "var(--accent)", fontWeight: 600 }}>{selectedIds.size} selected</span>
                            </>
                        )}
                        {advFilters.location_id && (
                            <>
                                <span style={{ color: "#d1d5db", fontSize: 14, lineHeight: 1 }}>·</span>
                                <span style={{ fontSize: 11.5, color: "var(--accent)", fontWeight: 600 }}>
                                    📍 {selectedLocation?.name || "Location filtered"}
                                </span>
                            </>
                        )}
                        {(advFilters.from_date || advFilters.to_date) && (
                            <>
                                <span style={{ color: "#d1d5db", fontSize: 14, lineHeight: 1 }}>·</span>
                                <span style={{ fontSize: 11.5, color: "var(--accent)", fontWeight: 600 }}>
                                    📅 {advFilters.from_date || "…"} → {advFilters.to_date || "…"}
                                </span>
                            </>
                        )}
                        {/* ── Active cascade tag filter chip ── */}
                        {activeFilterStepCount > 0 && (
                            <>
                                <span style={{ color: "#d1d5db", fontSize: 14, lineHeight: 1 }}>·</span>
                                <span style={{ fontSize: 11.5, color: "#4338ca", fontWeight: 600 }}>
                                    🏷️ {activeFilterStepCount} tag filter{activeFilterStepCount > 1 ? "s" : ""} active
                                </span>
                            </>
                        )}
                    </div>

                    <div style={{
                        background: "var(--surface)",
                        borderRadius: 12,
                        border: "1px solid #e2e6ea",
                        overflow: "hidden",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.05)",
                    }}>
                        {/* ── Column headers ── */}
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "10px 0",
                            background: "#f4f6f9",
                            borderBottom: "2px solid #e5e8ed",
                            position: "sticky",
                            top: 0,
                            zIndex: 10,
                        }}>
                            <div style={{ width: 46, flexShrink: 0, display: "flex", justifyContent: "center" }}>
                                <input type="checkbox" className="kpi-checkbox" checked={allSelected} onChange={toggleAll} title={allSelected ? "Deselect all" : "Select all"} />
                            </div>
                            <div style={{ width: 32, flexShrink: 0 }} />
                            <ColLabel flex={1}>KPI</ColLabel>
                            <ColLabel width={150}>Assigned To</ColLabel>
                            <ColLabel width={130}>Assigned By</ColLabel>
                            <ColLabel width={120}>Value</ColLabel>
                            <div style={{ width: 44, flexShrink: 0 }} />
                        </div>

                        {/* ── Loading skeleton ── */}
                        {isLoading && (
                            <div>
                                {[1, 2, 3, 4, 5, 6].map((i) => (
                                    <div key={i} style={{ display: "flex", alignItems: "center", padding: "18px 20px", borderBottom: "1px solid var(--rule-2)", gap: 14, opacity: 1 - i * 0.1 }}>
                                        <div style={{ width: 16, height: 16, borderRadius: 4, background: "#eaecf0", flexShrink: 0 }} />
                                        <div style={{ width: 20, height: 12, borderRadius: 4, background: "#eaecf0", flexShrink: 0 }} />
                                        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}>
                                            <div className="kpi-shimmer" style={{ height: 13, borderRadius: 4, width: `${60 + (i * 11) % 35}%`, background: "#eaecf0" }} />
                                            <div style={{ height: 10, borderRadius: 4, background: "#f1f3f6", width: "28%" }} />
                                        </div>
                                        {[130, 110, 100, 88].map((w) => (
                                            <div key={w} style={{ width: w, height: 26, borderRadius: 6, background: "#eaecf0", flexShrink: 0 }} />
                                        ))}
                                        <div style={{ width: 68, height: 22, borderRadius: 20, background: "#eaecf0", flexShrink: 0 }} />
                                        <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#eaecf0", flexShrink: 0 }} />
                                    </div>
                                ))}
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "14px 24px", gap: 10, color: "var(--ink-4)", fontSize: 12, fontFamily: "var(--font-sans)", borderTop: "1px solid var(--rule-2)" }}>
                                    <div className="kpi-spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                                    Loading KPIs…
                                </div>
                            </div>
                        )}

                        {/* ── Error state ── */}
                        {!isLoading && error && (
                            <div style={{ padding: "52px 24px", textAlign: "center", fontSize: 13 }}>
                                <div style={{ fontSize: 34, marginBottom: 12 }}>⚠️</div>
                                <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 14, color: "var(--red)" }}>Failed to load data</div>
                                <div style={{ color: "var(--ink-4)", fontSize: 12.5, marginBottom: 20 }}>{error}</div>
                                <button className="kpi-btn-secondary" onClick={() => refetch()}>↻ Retry</button>
                            </div>
                        )}

                        {/* ── Empty state ── */}
                        {!isLoading && !error && filtered.length === 0 && (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "68px 24px", gap: 10 }}>
                                <span style={{ fontSize: 40, opacity: 0.35 }}>📊</span>
                                <div style={{ fontWeight: 700, fontSize: 14, color: "var(--ink-2)", fontFamily: "var(--font-sans)" }}>No KPIs found</div>
                                <div style={{ fontSize: 12.5, color: "var(--ink-4)" }}>
                                    {activeFilterStepCount > 0
                                        ? "No KPIs match the selected tags. Try selecting different tags or clear the tag filter."
                                        : "Try adjusting the filters or search term."}
                                </div>
                                {hasAnyFilter && (
                                    <button className="kpi-btn-secondary" onClick={clearAllFilters} style={{ marginTop: 8 }}>Clear all filters</button>
                                )}
                            </div>
                        )}

                        {/* ── KPI rows ── */}
                        {!isLoading && !error && filtered.map((item, index) => (
                            <KpiRow
                                key={item.id}
                                item={item}
                                index={index}
                                selected={selectedIds.has(item.id)}
                                onToggle={() => toggleOne(item.id)}
                                searchQuery={search}
                                expanded={expandedId === item.id}
                                onExpand={() => setExpandedId(prev => prev === item.id ? null : item.id)}
                                onAdd={(i) => openModal("add", i)}
                                onHistory={handleOpenHistory}
                                onChat={(i) => openModal("chat", i)}
                                onAudit={(i) => openModal("audit", i)}
                                onAssign={(i) => openModal("assign", i)}
                                isEven={index % 2 === 0}
                            />
                        ))}

                        {/* ── Footer summary ── */}
                        {!isLoading && !error && filtered.length > 0 && (
                            <div style={{
                                padding: "11px 20px",
                                borderTop: "1px solid var(--rule-2)",
                                background: "#f8f9fb",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}>
                                <span style={{ fontSize: 11.5, color: "var(--ink-4)", fontFamily: "var(--font-mono)" }}>
                                    Showing {filtered.length} of {kpiList.length} KPIs
                                    {activeFilterStepCount > 0 && (
                                        <span style={{ marginLeft: 6, color: "#4338ca" }}>
                                            · {activeFilterStepCount} tag filter{activeFilterStepCount > 1 ? "s" : ""} active
                                        </span>
                                    )}
                                </span>
                                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, color: "var(--green)", fontWeight: 600 }}>
                                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--green)", display: "inline-block" }} />
                                        {completedCount} done
                                    </span>
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, color: "var(--amber)", fontWeight: 600 }}>
                                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--amber)", display: "inline-block" }} />
                                        {pendingCount} pending
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {/* ── Modals ── */}
            <KpiFiltersModal
                show={showFiltersModal}
                onHide={() => setShowFiltersModal(false)}
                filters={advFilters}
                onChange={advFilterChange}
                onApply={() => { }}
                onReset={() => setAdvFilters({ location_id: "", status: "", from_date: "", to_date: "" })}
                locations={locations}
            />

            <AddDataModal
                show={modal.type === "add"}
                onHide={closeModal}
                item={modal.item}
                financialYearId={selectedFY?.id}
                locations={locations}
                saveAnswer={saveAnswer}
                onSave={(item, data) => {
                    console.log("[AddData] saved", item.id, data);
                    refetch();
                }}
            />

            <ChatModal
                show={modal.type === "chat"}
                onHide={closeModal}
                item={modal.item}
                questionId={modal.item?.id}
                financialYearId={modal.item?.financial_year_id}
                sourceId={modal.item?.location_id}
                fromDate={modal.item?.from_date}
                toDate={modal.item?.to_date}
                currentUserId={JSON.parse(localStorage.getItem("currentUser") || "null")?.id ?? null}
                userForRole={JSON.parse(localStorage.getItem("currentUser") || "null") ?? {}}
                participants={[]}
                initialMessages={[]}
                onSend={(msg, recipients) => console.log("[Chat] send", msg, recipients)}
            />

            <AuditTrailModal
                show={modal.type === "audit"}
                onHide={closeModal}
                item={modal.item}
                financialYearId={selectedFY?.id}
            />

            <AssignModal
                show={modal.type === "assign"}
                onHide={closeModal}
                item={modal.item}
                assignDetailsFromApi={modal.item?.assign_raw || null}
                sources={locations}
            />

            <BulkAssignModal
                show={bulkModal === "assign"}
                onHide={closeBulkModal}
                selectedIds={selectedIdsArray}
                financialYearId={selectedFY?.id}
                managementListValue={managementListValue}
                onSuccess={() => { clearSelection(); refetch(); }}
            />

            <BulkReassignModal
                show={bulkModal === "reassign"}
                onHide={closeBulkModal}
                selectedIds={selectedIdsArray}
                financialYearId={selectedFY?.id}
                managementListValue={managementListValue}
                onSuccess={() => { clearSelection(); refetch(); }}
            />

            <BulkPrefillModal
                show={bulkModal === "prefill"}
                onHide={closeBulkModal}
                selectedIds={selectedIdsArray}
                financialYears={financialYears}
                selectedFinancialYearId={selectedFY?.id}
                onSuccess={() => { clearSelection(); refetch(); }}
            />

            <BulkReminderModal
                show={bulkModal === "reminder"}
                onHide={closeBulkModal}
                selectedIds={selectedIdsArray}
                onSuccess={() => clearSelection()}
            />

            <BulkAuditAssignModal
                show={bulkModal === "auditAssign"}
                onHide={closeBulkModal}
                selectedIds={selectedIdsArray}
                financialYearId={selectedFY?.id}
                locationId={advFilters.location_id || null}
                companyId={JSON.parse(localStorage.getItem("currentUser") || "null")?.companyId ?? null}
                managementListValue={auditorType}
                onSuccess={() => { clearSelection(); refetch(); }}
            />
        </div>
    );
};

export default QuantativeList;